"use client";
import React, {
  forwardRef,
  useImperativeHandle,
  useCallback,
  useRef,
  useEffect,
  useState,
  useMemo,
} from "react";
import log from "logbeacon";
import { cn } from "utils";
import { Icon } from "../Icon";
import { ospThemes } from "../ospThemes";
import { GroupedVirtualListProps, GroupedVirtualListRef } from "./types";
import { useGroupedData } from "./useGroupedData";
import { useItemHeights } from "./useItemHeights";
import { useResizeObserver } from "./useResizeObserver";
import { useVirtualScroll } from "./useVirtualScroll";

/**
 * GroupedVirtualList 组件
 * 通用的分组虚拟列表，支持分组展示、吸顶效果、无限滚动、独立展开/折叠、
 * 动态插入数据和自动去重功能
 */
function GroupedVirtualListInner<T>(
  props: GroupedVirtualListProps<T>,
  ref: React.Ref<GroupedVirtualListRef<T>>,
) {
  const {
    data,
    renderItem,
    renderGroupHeader,
    getItemKey,
    itemHeight,
    expandedItemHeight,
    getItemHeight,
    estimatedItemHeight = 164,
    enableAutoHeightDetection = false,
    enableVirtualScroll = true,
    overscan = 3,
    debugVirtualScroll = false,
    hideScrollbar = true,
    groupHeaderHeight = 40,
    stickyTopOffset = 0,
    hasNextPage = false,
    onEndReached,
    onEndReachedThreshold = 100,
    insertAnimation = 'slide',
    renderInsertAnimation,
    checkDuplicateGroups = 3,
    expandedItemKeys,
    onItemToggle,
    expandTransitionDuration = 300,
    itemGap = 0,
    groupGap = 0,
    groupToItemGap,
    onDuplicateRemoved,
    renderLoading,
    renderNoMore,
    className = '',
    style,
    loadingContainerClassName,
    noMoreContainerClassName,
    showBackToTop = true,
    backToTopThreshold = 200,
    itemClass,
  } = props;

  // 如果未指定 groupToItemGap，则使用 itemGap 的值
  const actualGroupToItemGap = groupToItemGap ?? itemGap;

  // 将 expandedItemKeys 规范化为 Set，便于快速查找
  const expandedItemKeysSet = useMemo(() => {
    if (!expandedItemKeys) {
      return new Set<string>();
    }
    if (expandedItemKeys instanceof Set) {
      return expandedItemKeys;
    }
    return new Set(expandedItemKeys);
  }, [expandedItemKeys]);

  // 向后兼容：计算默认展开高度
  const defaultExpandedHeight = expandedItemHeight ?? (itemHeight ? itemHeight * 2 : undefined);

  // 使用高度管理 hook
  const {
    getHeight,
    updateHeight,
    clearCache,
    clearItemCache,
    forceUpdate,
  } = useItemHeights<T>({
    getItemHeight,
    estimatedItemHeight,
    defaultItemHeight: itemHeight,
    defaultExpandedItemHeight: defaultExpandedHeight,
  });

  // 使用 ResizeObserver 自动检测高度变化
  const { setObservedElement } = useResizeObserver({
    enabled: enableAutoHeightDetection,
    onHeightChange: updateHeight,
  });

  // 使用数据处理 hook
  const {
    flattenedData,
    insertItem,
    insertItems,
    isItemInserted,
    getGroupItems,
    updateItem,
  } = useGroupedData(data, getItemKey, checkDuplicateGroups, onDuplicateRemoved);

  // 容器引用
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLElement>(null);

  // 使用虚拟滚动 hook（如果启用）
  const virtualScrollResult = useVirtualScroll<T>({
    flattenedData,
    getHeight,
    groupHeaderHeight,
    itemGap,
    groupGap,
    groupToItemGap: actualGroupToItemGap,
    expandedItemKeys: expandedItemKeysSet,
    containerRef,
    overscan,
    debug: debugVirtualScroll,
  });

  // 根据是否启用虚拟滚动，选择要渲染的数据
  const itemsToRender = enableVirtualScroll ? virtualScrollResult.virtualItems : flattenedData;

  // 用于控制"回到顶部"按钮的显示/隐藏
  const [showBackToTopButton, setShowBackToTopButton] = useState(false);

  // 用于追踪当前应该吸顶的分组
  const [currentStickyGroup, setCurrentStickyGroup] = useState<string | null>(null);

  /**
   * 滚动到列表顶部
   */
  const scrollToTop = useCallback((smooth: boolean = true) => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: 0,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }
  }, []);

  /**
   * 使用理论高度计算吸顶header（作为后备方案）
   * 当 DOM 中没有足够的 header 时使用
   */
  const calculateStickyHeaderTheoretical = useCallback((stopAtGroupKey?: string): string | null => {
    const scrollTop = containerRef.current?.scrollTop ?? 0;
    const triggerPoint = scrollTop + stickyTopOffset;

    let currentPosition = 0;
    let currentGroup: string | null = null;
    let lastMarginBottom = 0;
    let isFirstGroup = true;

    for (let i = 0; i < flattenedData.length; i++) {
      const flatItem = flattenedData[i];
      
      if (flatItem.type === 'header') {
        const headerGroupKey = flatItem.groupKey;
        
        // 如果遇到 stopAtGroupKey，停止理论计算
        if (stopAtGroupKey && headerGroupKey === stopAtGroupKey) {
          break;
        }
        
        let headerMarginTop = 0;
        if (!isFirstGroup) {
          headerMarginTop = groupGap;
        }

        const actualGap = Math.max(lastMarginBottom, headerMarginTop);
        currentPosition += actualGap;
        const headerStartPosition = currentPosition;
        currentPosition += groupHeaderHeight;
        lastMarginBottom = actualGroupToItemGap;

        if (headerStartPosition <= triggerPoint) {
          currentGroup = headerGroupKey;
        } else {
          break;
        }

        isFirstGroup = false;
      } else if (flatItem.type === 'item' && flatItem.itemKey && flatItem.data) {
        currentPosition += lastMarginBottom;
        const isExpanded = expandedItemKeysSet.has(flatItem.itemKey);
        const itemHeightValue = getHeight(flatItem.itemKey, flatItem.data, isExpanded);
        currentPosition += itemHeightValue;
        lastMarginBottom = itemGap;
      }
    }

    return currentGroup;
  }, [
    flattenedData,
    stickyTopOffset,
    groupHeaderHeight,
    groupGap,
    actualGroupToItemGap,
    itemGap,
    expandedItemKeysSet,
    getHeight,
  ]);

  /**
   * 计算当前应该吸顶的分组header
   * 策略：优先使用 DOM 实际位置，对于被虚拟化的 header 使用理论计算作为后备
   */
  const calculateStickyHeader = useCallback(() => {
    if (!containerRef.current || flattenedData.length === 0) {
      return null;
    }

    const scrollTop = containerRef.current.scrollTop;
    const triggerPoint = scrollTop + stickyTopOffset;
    const containerRect = containerRef.current.getBoundingClientRect();

    if (debugVirtualScroll) {
      const containerHeight = containerRef.current.clientHeight;
      const viewportEnd = scrollTop + containerHeight;
      log.debug('[GroupedVirtualList] calculateStickyHeader start:', {
        scrollTop,
        stickyTopOffset,
        triggerPoint,
        containerHeight,
        viewportEnd,
        flattenedDataLength: flattenedData.length,
      });
    }

    // 收集 DOM 中所有 header 的实际位置
    const headerElements = containerRef.current.querySelectorAll<HTMLElement>('[data-group-key]');
    const domHeaders: { groupKey: string; top: number }[] = [];
    
    headerElements.forEach((el) => {
      const groupKey = el.dataset.groupKey;
      if (groupKey) {
        const rect = el.getBoundingClientRect();
        // 计算相对于滚动容器的实际位置（考虑滚动偏移）
        const top = rect.top - containerRect.top + scrollTop;
        domHeaders.push({ groupKey, top });
      }
    });

    // 按位置排序（确保顺序正确）
    domHeaders.sort((a, b) => a.top - b.top);

    if (debugVirtualScroll) {
      log.debug('[GroupedVirtualList] DOM headers:', domHeaders);
    }

    let currentGroup: string | null = null;

    if (domHeaders.length > 0) {
      const firstDomHeader = domHeaders[0];
      
      // 检查是否有被虚拟化的 header 在当前 DOM header 之前
      // 这种情况发生在用户滚动到列表中部，前面的 header 被虚拟化
      const firstGroupKey = flattenedData.find(item => item.type === 'header')?.groupKey;
      
      if (firstGroupKey && firstGroupKey !== firstDomHeader.groupKey && firstDomHeader.top > triggerPoint) {
        // 第一个 DOM header 还没到吸顶位置，且存在被虚拟化的 header
        // 使用理论计算找到应该吸顶的 header（在第一个 DOM header 之前停止）
        currentGroup = calculateStickyHeaderTheoretical(firstDomHeader.groupKey);
        
        if (debugVirtualScroll) {
          log.debug('[GroupedVirtualList] Using theoretical calculation for virtualized headers:', {
            firstDomHeaderGroupKey: firstDomHeader.groupKey,
            firstDomHeaderTop: firstDomHeader.top,
            triggerPoint,
            theoreticalResult: currentGroup,
          });
        }
      }
      
      // 在 DOM header 中找最后一个 top <= triggerPoint 的
      for (const header of domHeaders) {
        if (header.top <= triggerPoint) {
          currentGroup = header.groupKey;
          if (debugVirtualScroll) {
            log.debug('[GroupedVirtualList] DOM Header should stick:', {
              groupKey: header.groupKey,
              headerTop: header.top,
              triggerPoint,
            });
          }
        } else {
          if (debugVirtualScroll) {
            log.debug('[GroupedVirtualList] DOM Header not at sticky position yet:', {
              groupKey: header.groupKey,
              headerTop: header.top,
              triggerPoint,
              diff: header.top - triggerPoint,
            });
          }
          break;
        }
      }
    } else {
      // 没有 DOM header，完全使用理论计算
      currentGroup = calculateStickyHeaderTheoretical();
      
      if (debugVirtualScroll) {
        log.debug('[GroupedVirtualList] No DOM headers, using theoretical calculation:', currentGroup);
      }
    }

    if (debugVirtualScroll) {
      log.debug('[GroupedVirtualList] calculateStickyHeader result:', currentGroup);
    }

    return currentGroup;
  }, [
    flattenedData,
    stickyTopOffset,
    debugVirtualScroll,
    calculateStickyHeaderTheoretical,
  ]);

  // 使用 ref 来节流 sticky header 的更新
  const stickyHeaderRafRef = useRef<number | undefined>(undefined);
  const lastStickyUpdateTimeRef = useRef<number>(0);

  /**
   * 处理滚动事件，控制"回到顶部"按钮的显示和吸顶header
   */
  const handleScroll = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    const scrollTop = containerRef.current.scrollTop;

    // 更新"回到顶部"按钮显示状态
    if (showBackToTop) {
      setShowBackToTopButton(scrollTop > backToTopThreshold);
    }

    // 节流 sticky header 的计算（最多每 16ms 更新一次，约 60fps）
    const now = Date.now();
    if (now - lastStickyUpdateTimeRef.current < 16) {
      return;
    }

    if (stickyHeaderRafRef.current !== undefined) {
      cancelAnimationFrame(stickyHeaderRafRef.current);
    }

    stickyHeaderRafRef.current = requestAnimationFrame(() => {
      lastStickyUpdateTimeRef.current = Date.now();
      const stickyGroup = calculateStickyHeader();
      if (debugVirtualScroll) {
        log.debug('[GroupedVirtualList] Sticky header update:', {
          scrollTop,
          stickyGroup,
        });
      }
      setCurrentStickyGroup(stickyGroup);
      stickyHeaderRafRef.current = undefined;
    });
  }, [showBackToTop, backToTopThreshold, calculateStickyHeader, debugVirtualScroll]);

  /**
   * 监听滚动事件
   */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    container.addEventListener('scroll', handleScroll, { passive: true });
    // 初始化时检查一次
    handleScroll();

    return () => {
      container.removeEventListener('scroll', handleScroll);
      // 清理 RAF
      if (stickyHeaderRafRef.current !== undefined) {
        cancelAnimationFrame(stickyHeaderRafRef.current);
      }
    };
  }, [handleScroll]);

  /**
   * 当数据变化时，重新计算吸顶header（仅在启用时）
   */
  useEffect(() => {
    const stickyGroup = calculateStickyHeader();
    setCurrentStickyGroup(stickyGroup);
  }, [flattenedData, calculateStickyHeader]);

  // 暴露给父组件的方法
  useImperativeHandle(ref, () => ({
    insertItem,
    insertItems,
    scrollToTop,
    updateItemHeight: (itemKey: string, height: number, isExpanded?: boolean) => {
      updateHeight(itemKey, height, isExpanded);
    },
    updateItem: (itemKey: string, updater: (item: T) => T) => {
      updateItem(itemKey, updater);
    },
    recalculateHeights: () => {
      clearCache();
      forceUpdate();
    },
    clearItemHeightCache: (itemKey: string) => {
      clearItemCache(itemKey);
    },
  }), [insertItem, insertItems, scrollToTop, updateHeight, updateItem, clearCache, clearItemCache, forceUpdate]);

  // 使用 ref 存储 expandedItemKeysSet，避免 handleToggleExpand 频繁重新创建
  const expandedItemKeysSetRef = useRef(expandedItemKeysSet);
  expandedItemKeysSetRef.current = expandedItemKeysSet;

  /**
   * 处理展开/折叠切换
   * 每个 item 独立控制展开/折叠状态
   */
  const handleToggleExpand = useCallback(
    (itemKey: string) => {
      // 清除该项的高度缓存
      clearItemCache(itemKey);
      
      // 计算切换后的状态并通知父组件
      const isCurrentlyExpanded = expandedItemKeysSetRef.current.has(itemKey);
      onItemToggle?.(itemKey, !isCurrentlyExpanded);
    },
    [onItemToggle, clearItemCache],
  );

  // 使用 ref 存储函数，避免依赖变化导致重新渲染
  const isItemInsertedRef = useRef(isItemInserted);
  isItemInsertedRef.current = isItemInserted;

  const renderItemRef = useRef(renderItem);
  renderItemRef.current = renderItem;

  const renderInsertAnimationRef = useRef(renderInsertAnimation);
  renderInsertAnimationRef.current = renderInsertAnimation;

  /**
   * 获取插入动画的类名
   */
  const getInsertAnimationClass = useCallback((itemKey: string): string => {
    if (!isItemInsertedRef.current(itemKey)) { return ''; }

    switch (insertAnimation) {
      case 'slide':
        return 'grouped-virtual-list-insert-slide';
      case 'fade':
        return 'grouped-virtual-list-insert-fade';
      default:
        return '';
    }
  }, [insertAnimation]);

  /**
   * 记忆化的列表项组件
   * 使用 React.memo 避免不必要的重新渲染
   */
  const MemoizedListItem = useMemo(() => {
    const ItemComponent = ({ item, itemKey, index, isExpanded, className }: {
      item: T;
      itemKey: string;
      index: number;
      isExpanded: boolean; // 将展开状态作为 prop 传入
      className?: string;
    }) => {
      // 使用动态高度计算
      const currentHeight = getHeight(itemKey, item, isExpanded);
      const animationClass = getInsertAnimationClass(itemKey);

      const itemContent = renderItemRef.current(
        item,
        index,
        isExpanded,
        () => handleToggleExpand(itemKey),
      );

      // 如果使用自定义插入动画
      const customAnimation = renderInsertAnimationRef.current;
      if (insertAnimation === 'custom' && isItemInsertedRef.current(itemKey) && customAnimation) {
        return (
          <div
            ref={enableAutoHeightDetection ? setObservedElement(itemKey) : undefined}
            data-item-key={itemKey}
            data-tracking-scope="grouped-virtual-list-item"
            className={className}
            style={{
              minHeight: currentHeight,
              marginBottom: itemGap,
              transition: `min-height ${expandTransitionDuration}ms ease-in-out`,
              overflow: enableAutoHeightDetection ? 'visible' : 'hidden',
            }}
          >
            {customAnimation(itemContent)}
          </div>
        );
      }

      return (
        <div
          ref={enableAutoHeightDetection ? setObservedElement(itemKey) : undefined}
          data-item-key={itemKey}
          data-tracking-scope="grouped-virtual-list-item"
          className={cn(animationClass, className)}
          style={{
            minHeight: currentHeight,
            marginBottom: itemGap,
            transition: `min-height ${expandTransitionDuration}ms ease-in-out`,
            overflow: enableAutoHeightDetection ? 'visible' : 'hidden',
          }}
        >
          {itemContent}
        </div>
      );
    };

    ItemComponent.displayName = 'GroupedVirtualListItem';

    return React.memo(
      ItemComponent,
      // 自定义比较函数：只有当关键属性变化时才重新渲染
      (prevProps, nextProps) => {
        // 如果 itemKey 不同，一定要重新渲染
        if (prevProps.itemKey !== nextProps.itemKey) {
          return false;
        }
        
        // 检查展开状态是否变化
        if (prevProps.isExpanded !== nextProps.isExpanded) {
          return false;
        }
        
        // 检查 item 数据是否变化（浅比较）
        if (prevProps.item !== nextProps.item) {
          return false;
        }
        
        // 其他情况不重新渲染
        return true;
      },
    );
  }, [
    getHeight,
    getInsertAnimationClass,
    handleToggleExpand,
    insertAnimation,
    expandTransitionDuration,
    itemGap,
    enableAutoHeightDetection,
    setObservedElement,
  ]);

  /**
   * 渲染分组标题
   */
  const renderHeader = useCallback((groupKey: string, isFirstGroup: boolean) => {
    const items = getGroupItems(groupKey);

    const headerContent = renderGroupHeader
      ? renderGroupHeader(groupKey, items)
      : <div className="px-4 py-2 font-semibold">{groupKey}</div>;

    // 当这个header正在吸顶时，设置为透明以避免重复显示（仅在启用吸顶时）
    const isSticky = currentStickyGroup === groupKey;

    return (
      <div
        key={`header-${groupKey}`}
        data-group-key={groupKey}
        style={{
          height: groupHeaderHeight,
          marginTop: isFirstGroup ? 0 : groupGap,
          marginBottom: actualGroupToItemGap,
          opacity: isSticky ? 0 : 1,
          transition: 'opacity 0.15s ease-in-out',
        }}
      >
        {headerContent}
      </div>
    );
  }, [renderGroupHeader, getGroupItems, groupHeaderHeight, groupGap, actualGroupToItemGap, currentStickyGroup]);

  /**
   * 组件挂载后添加事件监听
   */
  const setListRef = useCallback((node: HTMLElement | null) => {
    // 移除旧引用的事件监听
    if (listRef.current && onEndReached) {
      listRef.current.removeEventListener("end-reached", onEndReached as EventListener);
    }

    // 设置新引用并添加事件监听
    if (node && onEndReached) {
      listRef.current = node;
      node.addEventListener("end-reached", onEndReached as EventListener);
    }
  }, [onEndReached]);

  /**
   * 使用useMemo缓存固定header的内容
   */
  const stickyHeaderContent = useMemo(() => {
    if (!currentStickyGroup) {
      return null;
    }

    const items = getGroupItems(currentStickyGroup);
    return renderGroupHeader
      ? renderGroupHeader(currentStickyGroup, items)
      : <div className="px-4 py-2 font-semibold">{currentStickyGroup}</div>;
  }, [currentStickyGroup, getGroupItems, renderGroupHeader]);

  /**
   * 渲染扁平化后的列表（支持虚拟滚动）
   */
  const renderFlattenedList = useCallback((className?: string) => {
    const elements: React.ReactNode[] = [];

    // 如果启用虚拟滚动，添加上方占位符
    if (enableVirtualScroll && virtualScrollResult.topPlaceholderHeight > 0) {
      elements.push(
        <div
          key="top-placeholder"
          style={{ height: virtualScrollResult.topPlaceholderHeight }}
        />,
      );
    }

    // 渲染可见项目
    // 找到第一个分组的key，用于判断是否是第一个分组
    const firstGroupKey = flattenedData.find(item => item.type === 'header')?.groupKey;
    
    itemsToRender.forEach((flatItem) => {
      if (flatItem.type === 'header') {
        // 判断是否是第一个分组：基于所有数据而不是可见项
        const isFirstGroup = flatItem.groupKey === firstGroupKey;
        elements.push(renderHeader(flatItem.groupKey, isFirstGroup));
      } else if (flatItem.type === 'item' && flatItem.data && flatItem.itemKey !== undefined) {
        const isExpanded = expandedItemKeysSet.has(flatItem.itemKey);
        elements.push(
          <MemoizedListItem
            key={flatItem.itemKey}
            item={flatItem.data}
            itemKey={flatItem.itemKey}
            index={flatItem.index || 0}
            isExpanded={isExpanded}
            className={className}
          />,
        );
      }
    });

    // 如果启用虚拟滚动，添加下方占位符
    if (enableVirtualScroll && virtualScrollResult.bottomPlaceholderHeight > 0) {
      elements.push(
        <div
          key="bottom-placeholder"
          style={{ height: virtualScrollResult.bottomPlaceholderHeight }}
        />,
      );
    }

    // 在 bottomPlaceholder 之后渲染 loading/noMore，确保它们在列表最底部
    if (hasNextPage && renderLoading) {
      elements.push(
        <div key="loading" className={loadingContainerClassName}>
          {renderLoading}
        </div>,
      );
    } else if (!hasNextPage && renderNoMore) {
      elements.push(
        <div key="no-more" data-nomore className={noMoreContainerClassName}>
          {renderNoMore}
        </div>,
      );
    }

    return elements;
  }, [
    enableVirtualScroll,
    virtualScrollResult.topPlaceholderHeight,
    virtualScrollResult.bottomPlaceholderHeight,
    itemsToRender,
    flattenedData,
    renderHeader,
    MemoizedListItem,
    expandedItemKeysSet,
    hasNextPage,
    renderLoading,
    renderNoMore,
    loadingContainerClassName,
    noMoreContainerClassName,
  ]);

  return (
    <>
      <style>
        {`
          @keyframes grouped-virtual-list-slide-in {
            from {
              opacity: 0;
              transform: translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes grouped-virtual-list-fade-in {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          
          .grouped-virtual-list-insert-slide {
            animation: grouped-virtual-list-slide-in 0.5s ease-out;
          }
          
          .grouped-virtual-list-insert-fade {
            animation: grouped-virtual-list-fade-in 0.5s ease-out;
          }
          
          /* 隐藏滚动条但保留滚动功能 */
          .grouped-virtual-list-hide-scrollbar {
            scrollbar-width: none; /* Firefox */
            -ms-overflow-style: none; /* IE 10+ */
          }
          
          .grouped-virtual-list-hide-scrollbar::-webkit-scrollbar {
            display: none; /* Chrome, Safari, Edge */
          }
        `}
      </style>
      <div
        className={`grouped-virtual-list-wrapper ${className}`}
        style={{ position: 'relative', ...style }}
      >
        {/* 固定定位的吸顶header层 - 放在外层，不受滚动影响（仅在启用时渲染） */}
        {currentStickyGroup && stickyHeaderContent && (
          <div
            className="absolute left-0 right-0"
            style={{
              top: stickyTopOffset,
              height: groupHeaderHeight,
              zIndex: 20,
              pointerEvents: 'none', // 不阻挡下层交互
            }}
            data-sticky-group={currentStickyGroup}
          >
            {stickyHeaderContent}
          </div>
        )}
        
        {/* 滚动容器 */}
        <div
          ref={containerRef}
          className={`grouped-virtual-list-container overflow-y-auto ${hideScrollbar ? 'grouped-virtual-list-hide-scrollbar' : ''}`}
          style={{ width: '100%', height: '100%' }}
        >
          {/* @ts-expect-error - Web Components 类型定义 */}
          <infinite-scroll-list
            ref={setListRef}
            has-next-page={hasNextPage ? "true" : "false"}
            on-end-reached-threshold={String(onEndReachedThreshold)}
          >
            {renderFlattenedList(itemClass)}
            {/* loading 和 noMore 已在 renderFlattenedList 中渲染，确保在 bottomPlaceholder 之后 */}
            {/* @ts-expect-error - Web Components 类型定义 */}
          </infinite-scroll-list>
        </div>
        
        {showBackToTop && showBackToTopButton && (
          <div
            className="w-11 h-11 md:flex hidden items-center justify-center absolute button_icon tonal_button px-[5px] py-1 bg-background_white_overlay10 rounded-full right-0 cursor-pointer z-11"
            style={{
              bottom: "88px",
              backdropFilter: "blur(50px)",
              willChange: 'backdrop-filter',
            }}
            onClick={() => {
              log.debug("[GroupedVirtualList] scrollToTop", scrollToTop);
              scrollToTop(true);
            }}
          >
            <Icon
              size={24}
              name="back-to-top"
              color={ospThemes.colors.content_primary}
            />
          </div>
        )}
      </div>
    </>
  );
}

// 使用 forwardRef 包装组件以支持泛型
const GroupedVirtualListComponent = forwardRef(GroupedVirtualListInner) as <T>(
  props: GroupedVirtualListProps<T> & { ref?: React.Ref<GroupedVirtualListRef<T>> }
) => React.ReactElement;

// 设置 displayName 用于调试
(GroupedVirtualListComponent as any).displayName = "GroupedVirtualList";

export const GroupedVirtualList = GroupedVirtualListComponent;

